import { Checkbox, Collapse, Divider, Form, Input, Select } from "antd";
import { ChevronDown, ChevronUp, PlusCircle } from "lucide-react";
import { FC, useEffect, useState, useRef } from "react";
import { useGetClients } from "~/services/client/client.queries";
import { useGetCountries } from "~/services/common";
import { capitalize } from "~/utils";
import { ActiveStatus, ClientStatus, ClientType } from "~/utils/constants";

type Props = {};

export const TabInfo: FC<Props> = () => {
  const form = Form.useFormInstance();
  const { data: countries, isLoading } = useGetCountries();
  const [expandedAddresses, setExpandedAddresses] = useState<Set<string>>(new Set(['0'])); // Always expand first address
  const previousFieldsLength = useRef<number>(0);

  const isSubClient = Form.useWatch("isSubClient", form);
  const isBillingParent = Form.useWatch("isBillingParent", form);
  const shippingAddresses = Form.useWatch("shippingAddresses", form);
  const createUserAccount = Form.useWatch("createUserAccount", form);

  // Ensure first address is always expanded when addresses exist
  useEffect(() => {
    if (shippingAddresses && shippingAddresses.length > 0) {
      setExpandedAddresses(prev => new Set([...prev, '0'])); // Always include first address
    }
  }, [shippingAddresses]);

  const copyShippingAddress = (idx: number) => {
    const shippingAddresses = form.getFieldValue(["shippingAddresses", idx]);
    if (shippingAddresses) {
      form.setFieldsValue({
        billingAddress: shippingAddresses,
      });
    }
  };

  const handleSubclientChange = (checked: boolean) => {
    if (!checked) {
      // Clear related fields when unchecking subclient
      form.setFieldsValue({
        parentClientId: undefined,
        isBillingParent: false,
      });
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Client Information</h2>

      <div className="space-y-6">
        {/* Company Information */}
        <div className="p-4 border rounded-lg bg-gray-50">
          <h3 className="font-medium mb-4">Company Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Form.Item
                name="companyName"
                rules={[
                  { required: true, message: "Company Name is required" },
                ]}
                label="Company Name"
              >
                <Input type="text" placeholder="Enter company name" />
              </Form.Item>
            </div>
            <div>
              <Form.Item
                name="type"
                rules={[{ required: true, message: "Type is required" }]}
                label="Type"
              >
                <Select
                  placeholder="Select type"
                  options={Object.values(ClientType).map((type) => ({
                    label: capitalize(type),
                    value: type,
                  }))}
                />
              </Form.Item>
            </div>
            <div>
              <Form.Item
                name="status"
                rules={[{ required: true, message: "Status is required" }]}
                label="Status"
              >
                <Select
                  placeholder="Select status"
                  options={Object.values(ActiveStatus).map((status) => ({
                    label: capitalize(status),
                    value: status,
                  }))}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* Subclient Configuration */}
        <div className="p-4 border rounded-lg bg-gray-50">
          <h3 className="font-medium mb-4">Client Relationship</h3>
          <div className="space-y-4">
            <Form.Item name="isSubClient" valuePropName="checked">
              <Checkbox
                onChange={(e) => handleSubclientChange(e.target.checked)}
              >
                This is a subclient
              </Checkbox>
            </Form.Item>

            {isSubClient && <SelectParentClient />}
          </div>
        </div>

        {/* Contact Information */}
        <div className="p-4 border rounded-lg bg-gray-50">
          <h3 className="font-medium mb-4">Primary Contact</h3>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="firstName"
              rules={[{ required: true, message: "First Name is required" }]}
              label="First Name"
            >
              <Input placeholder="Enter first name" />
            </Form.Item>
            <Form.Item
              name="lastName"
              rules={[{ required: true, message: "Last Name is required" }]}
              label="Last Name"
            >
              <Input placeholder="Enter last name" />
            </Form.Item>
            <Form.Item
              name="email"
              rules={[
                {
                  required: createUserAccount,
                  message: "Email is required when creating a user account"
                },
                { type: "email", message: "Please enter a valid email" },
              ]}
              label="Email"
            >
              <Input placeholder="Enter email address" />
            </Form.Item>
            <Form.Item
              name="createUserAccount"
              valuePropName="checked"
              style={{ marginTop: -10, marginBottom: 10 }}
            >
              <Checkbox>
                Create user account with this email
              </Checkbox>
            </Form.Item>
            <Form.Item
              name="contactPhone"
              label="Phone"
              extra={!createUserAccount && <span className="text-xs text-gray-500">Either email or phone is required</span>}
            >
              <Input placeholder="Enter phone number" />
            </Form.Item>
            <Form.Item name="companyDirectPhone" label="Company Direct Phone">
              <Input placeholder="Enter company phone" />
            </Form.Item>
            <Form.Item name="companyMobile" label="Company Mobile">
              <Input placeholder="Enter company mobile" />
            </Form.Item>
          </div>
        </div>
        <Form.List name="shippingAddresses">
          {(fields, { add }) => {
            // When a new field is added, expand it
            if (fields.length > previousFieldsLength.current) {
              const newFieldKey = fields[fields.length - 1]?.key.toString();
              if (newFieldKey) {
                setExpandedAddresses(prev => new Set([...prev, newFieldKey]));
              }
              previousFieldsLength.current = fields.length;
            }

            return (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium">Shipping Addresses</h3>
                  <button
                    type="button"
                    onClick={() => add()}
                    className="flex items-center text-blue-600 hover:text-blue-800"
                  >
                    <PlusCircle size={16} className="mr-1" />
                    Add Address
                  </button>
                </div>

                {/* Primary Shipping Address (Always Visible) */}
                <div className="p-4 border rounded-lg bg-gray-50 mb-4">
                    <div className="flex justify-between items-center mb-4">
                      <h4 className="font-medium">Primary Shipping Address</h4>
                      <button
                        type="button"
                        onClick={() => copyShippingAddress(0)}
                        className="text-blue-600 text-sm hover:text-blue-800"
                      >
                        Set as Billing Address
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Form.Item
                        name={[0, "addressName"]}
                        label="Address Name/Type"
                      >
                        <Input placeholder="e.g. Main Gallery, Warehouse, etc." />
                      </Form.Item>
                      <Form.Item
                        name={[0, "travelTime"]}
                        label="Travel Time (minutes)"
                        rules={[
                          { required: true, message: "Travel time is required" },
                        ]}
                      >
                        <Input
                          type="number"
                          placeholder="Estimated travel time to location"
                        />
                      </Form.Item>
                      <Form.Item
                        name={[0, "address"]}
                        rules={[
                          {
                            required: true,
                            message: "Address Line 1 is required",
                          },
                        ]}
                        label="Address Line 1"
                      >
                        <Input placeholder="Street address" />
                      </Form.Item>
                      <Form.Item
                        name={[0, "apt"]}
                        label="Address Line 2"
                      >
                        <Input placeholder="Apt, Suite, etc." />
                      </Form.Item>
                      <Form.Item
                        name={[0, "city"]}
                        rules={[
                          { required: true, message: "City is required" },
                        ]}
                        label="City"
                      >
                        <Input placeholder="City" />
                      </Form.Item>
                      <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                          name={[0, "state"]}
                          rules={[
                            {
                              required: true,
                              message: "State/Province is required",
                            },
                          ]}
                          label="State/Province"
                        >
                          <Input placeholder="State/Province" />
                        </Form.Item>
                        <Form.Item
                          name={[0, "zip"]}
                          label="Postal Code"
                          rules={[
                            { required: true, message: "Postal code is required" },
                          ]}
                        >
                          <Input placeholder="Postal/ZIP code" />
                        </Form.Item>
                      </div>
                      <Form.Item
                        name={[0, "country"]}
                        rules={[
                          {
                            required: true,
                            message: "Country is required",
                          },
                        ]}
                        label="Country"
                      >
                        <Select
                          placeholder="Select country"
                          loading={isLoading}
                          showSearch
                          optionFilterProp="value"
                          options={countries?.map((country) => ({
                            value: country.cca2,
                            label: (
                              <div className="flex items-center">
                                {country.flag} {country.name.common}
                              </div>
                            ),
                          }))}
                        />
                      </Form.Item>
                      <Form.Item
                        name={[0, "specialInstructions"]}
                        label="Special Instructions"
                      >
                        <Input.TextArea
                          placeholder="Loading dock info, access codes, etc."
                          rows={2}
                        />
                      </Form.Item>
                    </div>
                  </div>

                {/* Additional Shipping Addresses (Collapsible) */}
                {fields.slice(1).map((field) => {
                  const currentAddressName = shippingAddresses?.[field.name]?.addressName;
                  const addressLabel = currentAddressName
                    ? `Shipping Address ${field.name + 1} - ${currentAddressName}`
                    : `Shipping Address ${field.name + 1}`;

                  return (
                    <Collapse
                      className="mb-4"
                      key={field.key}
                      size="small"
                      expandIconPosition="end"
                      activeKey={expandedAddresses.has(field.key.toString()) ? [field.key.toString()] : []}
                      onChange={(keys) => {
                        const fieldKeyStr = field.key.toString();
                        const newExpanded = new Set(expandedAddresses);
                        if (keys.includes(fieldKeyStr)) {
                          newExpanded.add(fieldKeyStr);
                        } else {
                          newExpanded.delete(fieldKeyStr);
                        }
                        setExpandedAddresses(newExpanded);
                      }}
                      expandIcon={({ isActive }) =>
                        isActive ? (
                          <ChevronUp size={16} />
                        ) : (
                          <ChevronDown size={16} />
                        )
                      }
                      items={[
                        {
                          key: field.key.toString(),
                          label: (
                            <div className="font-medium">
                              {addressLabel}
                            </div>
                          ),
                        extra: (
                          <div className="flex items-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                copyShippingAddress(field.name);
                              }}
                              className="text-blue-600 text-sm hover:text-blue-800"
                            >
                              Set as Billing Address
                            </button>
                          </div>
                        ),
                        children: (
                          <div className="p-4">
                            <div className="grid grid-cols-2 gap-4">
                              <Form.Item
                                name={[field.name, "addressName"]}
                                label="Address Name/Type"
                              >
                                <Input placeholder="e.g. Main Gallery, Warehouse, etc." />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "travelTime"]}
                                label="Travel Time (minutes)"
                                rules={[
                                  { required: true, message: "Travel time is required" },
                                ]}
                              >
                                <Input
                                  type="number"
                                  placeholder="Estimated travel time to location"
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "address"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Address Line 1 is required",
                                  },
                                ]}
                                label="Address Line 1"
                              >
                                <Input placeholder="Street address" />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "apt"]}
                                label="Address Line 2"
                              >
                                <Input placeholder="Apt, Suite, etc." />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "city"]}
                                rules={[
                                  { required: true, message: "City is required" },
                                ]}
                                label="City"
                              >
                                <Input placeholder="City" />
                              </Form.Item>
                              <div className="grid grid-cols-2 gap-4">
                                <Form.Item
                                  name={[field.name, "state"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "State/Province is required",
                                    },
                                  ]}
                                  label="State/Province"
                                >
                                  <Input placeholder="State/Province" />
                                </Form.Item>
                                <Form.Item
                                  name={[field.name, "zip"]}
                                  label="Postal Code"
                                  rules={[
                                    { required: true, message: "Postal code is required" },
                                  ]}
                                >
                                  <Input placeholder="Postal/ZIP code" />
                                </Form.Item>
                              </div>
                              <Form.Item
                                name={[field.name, "country"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Country is required",
                                  },
                                ]}
                                label="Country"
                              >
                                <Select
                                  placeholder="Select country"
                                  loading={isLoading}
                                  showSearch
                                  optionFilterProp="value"
                                  options={countries?.map((country) => ({
                                    value: country.cca2,
                                    label: (
                                      <div className="flex items-center">
                                        {country.flag} {country.name.common}
                                      </div>
                                    ),
                                  }))}
                                />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, "specialInstructions"]}
                                label="Special Instructions"
                              >
                                <Input.TextArea
                                  placeholder="Loading dock info, access codes, etc."
                                  rows={2}
                                />
                              </Form.Item>
                            </div>
                          </div>
                        ),
                      },
                    ]}
                  />
                  );
                })}
              </div>
            );
          }}
        </Form.List>

        <Divider />

        <div className="p-4 border rounded-lg bg-gray-50">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">Billing Address</h3>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name={["billingAddress", "addressName"]}
              label="Address Name/Type"
            >
              <Input
                disabled={isBillingParent}
                placeholder="e.g. Main Gallery, Warehouse, etc."
              />
            </Form.Item>
           <Form.Item
  name={["billingAddress", "travelTime"]}
  label="Travel Time (minutes)"
>
  <Input
    disabled={isBillingParent}
    type="number"
    placeholder="Estimated travel time to location"
  />
</Form.Item>
            <Form.Item
              name={["billingAddress", "address"]}
              rules={[
                {
                  required: true,
                  message: "Address Line 1 is required",
                },
              ]}
              label="Address Line 1"
            >
              <Input disabled={isBillingParent} placeholder="Street address" />
            </Form.Item>
            <Form.Item name={["billingAddress", "apt"]} label="Address Line 2">
              <Input
                disabled={isBillingParent}
                placeholder="Apt, Suite, etc."
              />
            </Form.Item>
            <Form.Item
              name={["billingAddress", "city"]}
              rules={[{ required: true, message: "City is required" }]}
              label="City"
            >
              <Input disabled={isBillingParent} placeholder="City" />
            </Form.Item>
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name={["billingAddress", "state"]}
                rules={[
                  {
                    required: true,
                    message: "State/Province is required",
                  },
                ]}
                label="State/Province"
              >
                <Input
                  disabled={isBillingParent}
                  placeholder="State/Province"
                />
              </Form.Item>
             <Form.Item 
  name={["billingAddress", "zip"]} 
  label="Postal Code"
  rules={[
    { required: true, message: "Postal code is required" },
  ]}
>
  <Input
    disabled={isBillingParent}
    placeholder="Postal/ZIP code"
  />
</Form.Item>
            </div>
            <Form.Item
              name={["billingAddress", "country"]}
              rules={[
                {
                  required: true,
                  message: "Country is required",
                },
              ]}
              label="Country"
            >
              <Select
                disabled={isBillingParent}
                placeholder="Select country"
                loading={isLoading}
                showSearch
                optionFilterProp="value"
                options={countries?.map((country) => ({
                  value: country.cca2,
                  label: (
                    <div className="flex items-center">
                      {country.flag} {country.name.common}
                    </div>
                  ),
                }))}
              />
            </Form.Item>
            <Form.Item
              name={["billingAddress", "specialInstructions"]}
              label="Special Instructions"
            >
              <Input.TextArea
                disabled={isBillingParent}
                placeholder="Loading dock info, access codes, etc."
                rows={2}
              />
            </Form.Item>
          </div>
        </div>
      </div>
    </div>
  );
};

const SelectParentClient = () => {
  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: {
      isSubClient: false,
      status: ClientStatus.ACTIVE,
      sortByName: "ASC",
      getAll: true,
    },
  });

  const form = Form.useFormInstance();
  const parentClientId = Form.useWatch("parentClientId");

  useEffect(() => {
    const parentClient = clients?.items.find(
      (client) => client.id === parentClientId
    );
    if (parentClient) {
      const parentBillingAddress = parentClient.billingAddress || {};
      form.setFieldsValue({
        billingAddress: parentBillingAddress,
      });
    }
  }, [form, parentClientId, clients]);

  return (
    <>
      <Form.Item
        name="parentClientId"
        label="Parent Client"
        rules={[
          {
            required: true,
            message: "Parent client is required for subclients",
          },
        ]}
      >
        <Select
          placeholder="Select parent client"
          showSearch
          optionFilterProp="children"
          loading={clientsLoading}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          options={clients?.items.map((client) => ({
            value: client.id,
            label:
              client.companyName ||
              [client.firstName, client.lastName]
                .filter(Boolean)
                .join(" ")
                .trim() ||
              "Unnamed Client",
          }))}
        />
      </Form.Item>

      <Form.Item name="isBillingParent" valuePropName="checked">
        <Checkbox>Use parent client for billing and invoices</Checkbox>
      </Form.Item>
    </>
  );
};
